import { useMemo, useState, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import useLocalStorage from "use-local-storage";
import moment from "moment/moment";
import {
  IconArchive,
  IconBookmark,
  IconBookmarkFilled,
  IconHome,
  IconInfoCircle,
  IconMessage,
  IconPlus,
  IconUserSearch,
} from "@tabler/icons-react";
import styles from "./ShowCandidatList.module.css";
// @ts-expect-error pure js
import { ProfileStatus } from "../../models/profile";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
// @ts-expect-error pure js
import useMediaQuery from "../../hooks/use-media-query";
// @ts-expect-error pure js
import IsLoading from "../UI/IsLoading";
// @ts-expect-error pure js
import DisplayName from "../UI/DisplayName";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
// @ts-expect-error pure js
import { GLI_LINK } from "../../utils/constants/assurance";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import { useToast } from "../UI/Toast/useToast";
import Tooltip from "../UI/Tooltip";
import { Table } from "../UI/Table";
import Button from "../UI/Button";
import Card from "../UI/Card";
import Callout from "../UI/Callout";
import EmptyState from "../UI/EmptyState";
import IconButton from "../UI/IconButton";
import AvatarStack from "../UI/AvatarStack";
import Text from "../UI/Text";
import Status from "../UI/Status";
import FilterBar from "../UI/FilterBar/FilterBar";
import FilterSection, {
  FilterSelectSection,
} from "../UI/FilterBar/FilterSection";
import IndiceConfiance from "../Candidat/IndiceConfiance";
import { SuiviBlock } from "../Candidat/SuiviMenus";
import {
  formatEffortRate,
  getEffortRate,
  getEffortRateColor,
  LANDLORD_INTEREST_OPTIONS,
  VISIT_STATUS_OPTIONS,
  type FollowUp,
} from "../../utils/candidature";
import { getInitials } from "../Profile/profileUtils";
import type { Tenant } from "../../models/tenant";
import type { Profile } from "../Profile/types";
import type { Property } from "../../models/property";

const NON_RENSEIGNE = "Non renseigné";

interface CandidatWithMeta {
  tenant: Tenant;
  profiles: Profile[];
  property: Property | undefined;
}

function CandidatIdentity({
  profiles,
  propertyName,
}: {
  profiles: Profile[];
  propertyName?: string;
}) {
  return (
    <div className={styles.identity}>
      <AvatarStack
        items={profiles}
        getInitials={getInitials}
        getKey={(_, index) => index}
      />
      <div className={styles.identityNames}>
        {profiles.map((prof, i) => (
          <div key={i}>
            <Text as="div" weight="bold" className={styles.nameLine}>
              <DisplayName profile={prof} />
              {prof.presentation && (
                <Tooltip
                  content={<Text>{prof.presentation}</Text>}
                  direction="right"
                >
                  <IconMessage size={16} className={styles.presentationIcon} />
                </Tooltip>
              )}
            </Text>
            {prof.mail && (
              <Text as="div" variant="deci">
                {prof.mail}
              </Text>
            )}
          </div>
        ))}
        {propertyName && (
          <Text as="div" variant="deci">
            {propertyName}
          </Text>
        )}
      </div>
    </div>
  );
}

function TauxEffort({
  tenant,
  profiles,
  withLabel = false,
}: {
  tenant: Tenant;
  profiles: Profile[];
  withLabel?: boolean;
}) {
  const rate = getEffortRate(tenant, profiles);
  if (rate === null) {
    return <Text as="div">{NON_RENSEIGNE}</Text>;
  }
  const text = formatEffortRate(rate);
  return (
    <Status
      color={getEffortRateColor(rate)}
      text={withLabel ? `Effort ${text}` : text}
      weight="bold"
      className={styles.nowrap}
    />
  );
}

function FavoriButton({
  tenant,
  onToggle,
}: {
  tenant: Tenant;
  onToggle: () => void;
}) {
  return (
    <IconButton
      variant="plain"
      icon={tenant.favori ? IconBookmarkFilled : IconBookmark}
      label={tenant.favori ? "Retirer des favoris" : "Ajouter aux favoris"}
      color={tenant.favori ? "info" : undefined}
      requiresWrite
      onClick={(event: MouseEvent) => {
        event.stopPropagation();
        onToggle();
      }}
    />
  );
}

function CandidatRowDesktop({
  tenant,
  profiles,
  property,
  onClick,
  onToggleFavori,
  onChangeFollowUp,
}: CandidatWithMeta & CandidatRowActions) {
  return (
    <Table.Row onClick={onClick} className={styles.desktopRow}>
      <Table.Cell>
        <CandidatIdentity profiles={profiles} />
      </Table.Cell>

      <Table.Cell>
        <Text as="div" className={styles.propertyCell}>
          <IconHome size={16} />
          {property?.nom}
        </Text>
      </Table.Cell>

      <Table.Cell>
        <Text as="div">{moment(tenant.debut).format("D MMM YYYY")}</Text>
      </Table.Cell>

      <Table.Cell>
        <div className={styles.profileLines}>
          {profiles.map((prof, i) =>
            prof.status_pro ? (
              <Status key={i} color="info" text={prof.status_pro} />
            ) : (
              <Text key={i} as="div">
                {NON_RENSEIGNE}
              </Text>
            ),
          )}
        </div>
      </Table.Cell>

      <Table.Cell>
        <div className={styles.profileLines}>
          {profiles.map((prof, i) => (
            <Text key={i} as="div">
              {currencyFormatter(prof.revenus_brut) || NON_RENSEIGNE}
            </Text>
          ))}
          <TauxEffort tenant={tenant} profiles={profiles} withLabel />
        </div>
      </Table.Cell>

      <Table.Cell>
        <div className={styles.profileLines}>
          {profiles.map((prof, i) => (
            <Text key={i} as="div">
              {prof.caution_type ?? NON_RENSEIGNE}
            </Text>
          ))}
        </div>
      </Table.Cell>

      <Table.Cell>
        <IndiceConfiance
          tenant={tenant}
          profiles={profiles}
          propertyGli={property?.gli}
        />
      </Table.Cell>

      <Table.Cell>
        <SuiviBlock tenant={tenant} onChange={onChangeFollowUp} />
      </Table.Cell>

      <Table.Cell align="right">
        <FavoriButton tenant={tenant} onToggle={onToggleFavori} />
      </Table.Cell>
    </Table.Row>
  );
}

function CandidatRowMobile({
  tenant,
  profiles,
  property,
  onClick,
  onToggleFavori,
  onChangeFollowUp,
}: CandidatWithMeta & CandidatRowActions) {
  return (
    <Card padding="sm" onClick={onClick} className={styles.mobileCard}>
      <div className={styles.mobileTopRow}>
        <CandidatIdentity profiles={profiles} propertyName={property?.nom} />
        <FavoriButton tenant={tenant} onToggle={onToggleFavori} />
      </div>
      <Text as="div" variant="deci">
        Emménagement souhaité : {moment(tenant.debut).format("DD/MM/YYYY")}
      </Text>
      {profiles.map((prof, i) => (
        <div key={i} className={styles.mobileProfile}>
          <Text as="div" variant="deci">
            Statut professionnel : {prof.status_pro ?? NON_RENSEIGNE}
          </Text>
          <Text as="div" variant="deci">
            Revenus mensuels :{" "}
            {currencyFormatter(prof.revenus_brut) || NON_RENSEIGNE}
          </Text>
          <Text as="div" variant="deci">
            Type de caution : {prof.caution_type ?? NON_RENSEIGNE}
          </Text>
        </div>
      ))}
      <div className={styles.mobileIndicator}>
        <Text as="span" variant="deci">
          Taux d'effort :
        </Text>
        <TauxEffort tenant={tenant} profiles={profiles} />
      </div>
      <div className={styles.mobileIndicator}>
        <Text as="span" variant="deci">
          Indice de confiance :
        </Text>
        <IndiceConfiance
          tenant={tenant}
          profiles={profiles}
          propertyGli={property?.gli}
        />
      </div>
      <SuiviBlock tenant={tenant} onChange={onChangeFollowUp} />
    </Card>
  );
}

interface CandidatRowActions {
  onClick: () => void;
  onToggleFavori: () => void;
  onChangeFollowUp: (data: FollowUp) => void;
}

interface ShowCandidatListProps {
  tenants: Tenant[];
  isLoading: boolean;
  isArchived?: boolean;
  onAdd?: () => void;
}

export default function ShowCandidatList({
  tenants,
  isLoading,
  isArchived,
  onAdd,
}: ShowCandidatListProps) {
  const isSmallScreen = useMediaQuery("(max-width: 799px)");
  const navigate = useNavigate();
  const toast = useToast();
  const { properties } = useProperties();
  const { updateTenant } = useTenants() as {
    updateTenant: (id: number, data: Partial<Tenant>) => Promise<unknown>;
  };

  const [search, setSearch] = useState("");
  const storagePrefix = isArchived
    ? "candidatList_archives"
    : "candidatList_actifs";
  const [bienFilter, setBienFilter] = useLocalStorage<string[]>(
    `${storagePrefix}_bienFilter`,
    [],
  );
  const [statusProFilter, setStatusProFilter] = useLocalStorage<string[]>(
    `${storagePrefix}_statusProFilter`,
    [],
  );
  const [favoriFilter, setFavoriFilter] = useLocalStorage<string[]>(
    `${storagePrefix}_favoriFilter`,
    [],
  );
  const [interestFilter, setInterestFilter] = useLocalStorage<string[]>(
    `${storagePrefix}_interestFilter`,
    [],
  );
  const [visitFilter, setVisitFilter] = useLocalStorage<string[]>(
    `${storagePrefix}_visitFilter`,
    [],
  );

  const candidatsWithMeta: CandidatWithMeta[] = useMemo(
    () =>
      [...tenants]
        .sort((a, b) => Number(Boolean(b.favori)) - Number(Boolean(a.favori)))
        .map((tenant) => ({
          tenant,
          profiles: tenant.profiles.filter(
            (profile) =>
              profile.garantor_of === null &&
              profile.status === ProfileStatus.Actif.status,
          ),
          property: (properties as Property[])?.find(
            (prop) => prop.id === tenant.id_bien,
          ),
        }))
        .filter(({ profiles }) => profiles.length > 0),
    [tenants, properties],
  );

  const bienOptions = useMemo(() => {
    const bienIds = new Set(
      candidatsWithMeta.map(({ tenant }) => tenant.id_bien),
    );
    return [...((properties ?? []) as Property[])]
      .filter((prop) => bienIds.has(prop.id))
      .sort((a, b) => a.nom.localeCompare(b.nom))
      .map((prop) => ({ value: String(prop.id), label: prop.nom }));
  }, [properties, candidatsWithMeta]);

  // Purge les valeurs stockées qui ne correspondent plus à une option pour
  // éviter un filtre invisible.
  const activeBienFilter = useMemo(
    () =>
      bienFilter.filter((value) =>
        bienOptions.some((opt) => opt.value === value),
      ),
    [bienFilter, bienOptions],
  );

  const statusProOptions = useMemo(() => {
    const values = new Set(
      candidatsWithMeta.flatMap(({ profiles }) =>
        profiles
          .map((prof) => prof.status_pro)
          .filter((status): status is string => Boolean(status)),
      ),
    );
    return [...values].sort().map((value) => ({ value, label: value }));
  }, [candidatsWithMeta]);

  const activeStatusProFilter = useMemo(
    () =>
      statusProFilter.filter((value) =>
        statusProOptions.some((opt) => opt.value === value),
      ),
    [statusProFilter, statusProOptions],
  );

  const filteredCandidats = useMemo(() => {
    const searchLower = search.toLowerCase();
    return candidatsWithMeta.filter(({ tenant, profiles, property }) => {
      if (searchLower) {
        const matchesSearch =
          tenant.nom?.toLowerCase().includes(searchLower) ||
          property?.nom?.toLowerCase().includes(searchLower) ||
          profiles.some(
            (prof) =>
              prof.nom?.toLowerCase().includes(searchLower) ||
              prof.prenom?.toLowerCase().includes(searchLower) ||
              prof.mail?.toLowerCase().includes(searchLower),
          );
        if (!matchesSearch) return false;
      }
      if (
        activeBienFilter.length > 0 &&
        !activeBienFilter.includes(String(tenant.id_bien))
      ) {
        return false;
      }
      if (
        activeStatusProFilter.length > 0 &&
        !profiles.some(
          (prof) =>
            prof.status_pro && activeStatusProFilter.includes(prof.status_pro),
        )
      ) {
        return false;
      }
      if (
        interestFilter.length > 0 &&
        !interestFilter.includes(tenant.landlord_interest ?? "")
      ) {
        return false;
      }
      if (
        visitFilter.length > 0 &&
        !visitFilter.includes(tenant.visit_status ?? "")
      ) {
        return false;
      }
      return !(favoriFilter.length > 0 && !tenant.favori);
    });
  }, [
    candidatsWithMeta,
    search,
    activeBienFilter,
    activeStatusProFilter,
    favoriFilter,
    interestFilter,
    visitFilter,
  ]);

  const activeFiltersCount =
    (activeBienFilter.length > 0 ? 1 : 0) +
    (activeStatusProFilter.length > 0 ? 1 : 0) +
    (favoriFilter.length > 0 ? 1 : 0) +
    (interestFilter.length > 0 ? 1 : 0) +
    (visitFilter.length > 0 ? 1 : 0);

  function clearFilters() {
    setBienFilter([]);
    setStatusProFilter([]);
    setFavoriFilter([]);
    setInterestFilter([]);
    setVisitFilter([]);
  }

  async function changeFollowUp(tenant: Tenant, data: FollowUp) {
    try {
      await updateTenant(tenant.id, data);
    } catch (error) {
      toast.error({
        title: "Impossible d'enregistrer le suivi de la candidature",
        description: handleAPIError(error),
      });
    }
  }

  async function toggleFavori(tenant: Tenant) {
    try {
      await updateTenant(tenant.id, { favori: !tenant.favori });
    } catch (error) {
      toast.error({
        title: "Impossible de modifier les favoris",
        description: handleAPIError(error),
      });
    }
  }

  if (isLoading) return <IsLoading />;

  const banner = !isArchived && (
    <a
      className={styles.banner}
      href={GLI_LINK}
      target="_blank"
      rel="noreferrer"
    >
      <Callout
        variant="info"
        title="Protégez vos revenus locatifs dès aujourd'hui"
      >
        En un clic, souscrivez à une assurance loyers impayés, et louez l'esprit
        tranquille.
      </Callout>
    </a>
  );

  if (candidatsWithMeta.length === 0) {
    return (
      <>
        {banner}
        {isArchived ? (
          <EmptyState
            icon={IconArchive}
            title="Aucun candidat archivé"
            description="Les candidatures que vous archivez apparaîtront ici."
          />
        ) : (
          <EmptyState
            icon={IconUserSearch}
            title="Aucun candidat pour le moment"
            description="Ajoutez un candidat ou partagez votre lien de candidature pour recevoir les dossiers de vos futurs locataires."
          >
            {onAdd && (
              <Button icon={IconPlus} onClick={onAdd}>
                Ajouter un candidat
              </Button>
            )}
          </EmptyState>
        )}
      </>
    );
  }

  const filterBar = (
    <FilterBar
      searchValue={search}
      onSearchChange={setSearch}
      searchPlaceholder="Rechercher un candidat"
      activeFiltersCount={activeFiltersCount}
      onClearFilters={clearFilters}
      addLabel="Ajouter un candidat"
      onAdd={onAdd}
    >
      <FilterSection
        title="Favoris"
        options={[{ value: "favoris", label: "Favoris uniquement" }]}
        values={favoriFilter}
        onChange={setFavoriFilter}
        defaultOpen
      />
      <FilterSection
        title="Votre avis"
        options={LANDLORD_INTEREST_OPTIONS.map((option) => ({
          value: option.value,
          label: option.label,
        }))}
        values={interestFilter}
        onChange={setInterestFilter}
      />
      <FilterSection
        title="Visite"
        options={VISIT_STATUS_OPTIONS.map((option) => ({
          value: option.value,
          label: option.label,
        }))}
        values={visitFilter}
        onChange={setVisitFilter}
      />
      <FilterSection
        title="Situation professionnelle"
        options={statusProOptions}
        values={activeStatusProFilter}
        onChange={setStatusProFilter}
      />
      <FilterSelectSection
        title="Bien"
        options={bienOptions}
        values={activeBienFilter}
        onChange={setBienFilter}
      />
    </FilterBar>
  );

  if (filteredCandidats.length === 0) {
    return (
      <>
        {banner}
        {filterBar}
        <Card padding="lg" className={styles.emptyState}>
          <Text>Aucun candidat trouvé</Text>
        </Card>
      </>
    );
  }

  if (isSmallScreen) {
    return (
      <>
        {banner}
        {filterBar}
        <div className={styles.mobileContainer}>
          {filteredCandidats.map(({ tenant, profiles, property }) => (
            <CandidatRowMobile
              key={tenant.id}
              tenant={tenant}
              profiles={profiles}
              property={property}
              onClick={() => navigate(`/bailleur/candidats/${tenant.id}`)}
              onToggleFavori={() => toggleFavori(tenant)}
              onChangeFollowUp={(data) => changeFollowUp(tenant, data)}
            />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      {banner}
      {filterBar}
      <Table variant="card">
        <Table.Header>
          <Table.Column>Candidat</Table.Column>
          <Table.Column>Bien</Table.Column>
          <Table.Column>Emménagement</Table.Column>
          <Table.Column>Statut pro</Table.Column>
          <Table.Column>
            <div className={styles.columnInfoIcon}>
              Revenus
              <Tooltip
                content={
                  <>
                    <Text weight="bold">Revenus et taux d'effort</Text>
                    <Text>
                      Revenus mensuels déclarés par chaque candidat, puis taux
                      d'effort du dossier : part du loyer charges comprises dans
                      les revenus des candidats, garants exclus. Les bailleurs
                      et assureurs retiennent généralement un tiers des revenus
                      au maximum.
                    </Text>
                  </>
                }
              >
                <IconInfoCircle size={14} />
              </Tooltip>
            </div>
          </Table.Column>
          <Table.Column>Caution</Table.Column>
          <Table.Column>
            <div className={styles.columnInfoIcon}>
              Confiance
              <Tooltip
                content={
                  <>
                    <Text weight="bold">Indice de confiance</Text>
                    <Text>
                      Cet indice est attribué par Qalimo pour vous accompagner
                      dans le choix du locataire. Il est basé sur les revenus du
                      foyer face au loyer, la situation professionnelle et
                      l'ancienneté des candidats, et la garantie apportée.
                      Lorsque le bien est couvert par une assurance loyers
                      impayés (GLI), la garantie du candidat n'entre plus en
                      compte.
                    </Text>
                  </>
                }
              >
                <IconInfoCircle size={14} />
              </Tooltip>
            </div>
          </Table.Column>
          <Table.Column>Suivi</Table.Column>
          <Table.Column align="right" aria-label="Favori" />
        </Table.Header>
        <Table.Body>
          {filteredCandidats.map(({ tenant, profiles, property }) => (
            <CandidatRowDesktop
              key={tenant.id}
              tenant={tenant}
              profiles={profiles}
              property={property}
              onClick={() => navigate(`/bailleur/candidats/${tenant.id}`)}
              onToggleFavori={() => toggleFavori(tenant)}
              onChangeFollowUp={(data) => changeFollowUp(tenant, data)}
            />
          ))}
        </Table.Body>
      </Table>
    </>
  );
}
